<template>
  <div class="container">

    <!-- Balance Card -->
    <div class="balance-card">
      <div class="balance-header-row">
        <div class="balance-label">Баланс</div>
        <button @click="handlePaymentHistoryClick" class="history-btn">📋</button>
      </div>
      <div :class="['balance-amount', { 'skeleton': balance === null && !hasSessionData }]">
        {{ balance !== null ? `${balance} ₽` : '0 ₽' }}
      </div>
      <button class="balance-btn" @click="handleDepositClick">
        💳 Пополнить баланс
      </button>
    </div>

    <!-- Banners Section -->
    <div class="banners-section">
      <div
        class="banners-container"
        ref="bannersContainer"
        @touchstart="handleBannerTouchStart"
        @touchmove="handleBannerTouchMove"
        @touchend="handleBannerTouchEnd"
      >
        <div
          class="banners-track"
          :style="{ transform: `translateX(${bannerOffset}px)` }"
        >
          <div
            v-for="(banner, index) in banners"
            :key="index"
            class="banner-item"
            @click="handleBannerClick(banner)"
          >
            <img :src="banner.image" :alt="banner.title" class="banner-image" />
          </div>
        </div>

        <!-- Dots Indicator -->
        <div class="banner-dots">
          <div
            v-for="(banner, index) in banners"
            :key="index"
            :class="['dot', { active: currentBannerIndex === index }]"
            @click.stop="goToBanner(index)"
          ></div>
        </div>
      </div>
    </div>

    <!-- Swipeable Tariff Notification -->
    <div
      v-if="shouldShowTariffNotification && !isNotificationDismissed"
      class="tariff-notification"
      ref="notificationRef"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      :style="{ transform: `translateX(${swipeOffset}px)`, opacity: notificationOpacity }"
    >
      <div class="notification-icon">⚠️</div>
      <div class="notification-content">
        <div class="notification-title">{{ notificationTitle }}</div>
        <div class="notification-text">{{ notificationText }}</div>
      </div>
      <button class="notification-close" @click="dismissNotification">✕</button>
    </div>

    <!-- Active Subscriptions / White List -->
    <div class="card">
      <div class="card-header">
        <div class="card-title-wrapper">
          <!-- ✅ Табы переключения -->
          <div class="tabs-switcher">
            <button
              :class="['tab-btn', { active: activeTab === 'subscriptions' }]"
              @click="switchTab('subscriptions')"
            >
              Подписки
            </button>
            <button
              :class="['tab-btn', { active: activeTab === 'whitelist' }]"
              @click="switchTab('whitelist')"
            >
              Белые Списки
            </button>
          </div>
          <!-- ✅ Индикатор загрузки -->
          <div v-if="isRefreshing" class="refresh-indicator">
            <div class="spinner"></div>
          </div>
        </div>
      </div>

      <!-- Loading skeleton (только если нет данных в sessionStorage) -->
      <div v-if="isInitialLoading" class="sub-item skeleton" style="height: 180px;"></div>

      <template v-else>
        <!-- ========== ВКЛАДКА: ПОДПИСКИ ========== -->
        <template v-if="activeTab === 'subscriptions'">
          <!-- Free Tariff -->
          <div v-if="isFreeTariffAvailable" class="sub-item" ref="freeTariffRef">
            <div class="sub-header">
              <div class="sub-name">{{ freeTariff.tariff_name }}</div>
              <div class="sub-badge">Активна</div>
            </div>
            <div class="sub-info">
              <div class="sub-info-item">🌍 Базовые страны</div>
              <div class="sub-info-item">♾️ Безлимит</div>
            </div>
            <div class="sub-progress">
              <div class="progress-bar-bg">
                <div
                  class="progress-bar-fill"
                  :class="trafficBarClass"
                  :style="{ width: `${trafficPercentage}%` }"
                ></div>
              </div>
              <div class="progress-label">{{ freeTariff.tariff_left }}</div>
            </div>
            <button
              class="btn-small btn-primary"
              style="width: 100%;"
              @click="handleConnectionClick('FreeTariff')"
              ref="connectButtonRef"
            >
              🔗 Подключить
            </button>
          </div>

          <!-- Paid Tariffs -->
          <div
            v-for="(tariff, index) in uniqueTariffs"
            :key="index"
            class="sub-item"
          >
            <div class="sub-header">
              <div class="sub-name">{{ tariff.name }}</div>
              <div class="sub-badge" :class="{ 'inactive': tariff.daysRemaining <= 0 }">
                {{ tariff.daysRemaining > 0 ? 'Активна' : 'Истекла' }}
              </div>
            </div>
            <div class="sub-info">
              <div class="sub-info-item">🌍 {{ getCountriesText(tariff.name) }}</div>
              <div class="sub-info-item">♾️ Безлимит</div>
              <div class="sub-info-item">🖥 {{ getDevicesText(tariff.name) }}</div>
            </div>
            <div v-if="tariff.daysRemaining > 0" class="sub-progress">
              <div class="progress-bar-bg">
                <div
                  class="progress-bar-fill"
                  :style="{ width: `${calculateProgress(tariff.daysRemaining)}%` }"
                ></div>
              </div>
              <div class="progress-label">{{ tariff.subscriptionEnd }}</div>
            </div>
            <div v-else class="sub-info">
              <div class="sub-info-item">⏱ {{ tariff.subscriptionEnd }}</div>
            </div>
            <button
              v-if="tariff.daysRemaining > 0"
              class="btn-small btn-primary"
              style="width: 100%;"
              @click="handleConnectionClick(tariff.name)"
            >
              🔗 Подключить
            </button>
          </div>

          <!-- Empty state (только если нет данных И не идёт загрузка) -->
          <div v-if="!isFreeTariffAvailable && uniqueTariffs.length === 0" class="empty-state">
            <div class="empty-icon">📦</div>
            <div class="empty-title">Нет активных подписок</div>
            <div class="empty-text">Выберите тариф и начните пользоваться VPN</div>
            <button class="btn-large" @click="handleBuySubscriptionClick" style="margin-top: 16px;">
              Купить подписку
            </button>
          </div>
        </template>

        <!-- ========== ВКЛАДКА: БЕЛЫЕ СПИСКИ ========== -->
        <template v-else-if="activeTab === 'whitelist'">
          <div v-if="whiteList" class="sub-item whitelist-item">
            <div class="sub-header">
              <div class="sub-name">🌐 Белый список</div>
              <div class="sub-badge" :class="{ 'inactive': whiteList.gb_left <= 0 }">
                {{ whiteList.gb_left > 0 ? 'Активен' : 'Нет трафика' }}
              </div>
            </div>

            <!-- ✅ Индикатор трафика с анимацией -->
            <div class="traffic-indicator">
              <div class="traffic-label">
                <span class="traffic-amount">{{ whiteList.gb_left }} ГБ</span>
                <span class="traffic-total">из {{ whiteList.total_purchased }} ГБ</span>
              </div>
              <div class="traffic-bar-wrapper">
                <div class="traffic-bar-bg">
                  <div
                    class="traffic-bar-fill"
                    :class="{ 'animate': shouldAnimateWhitelist }"
                    :style="{ width: `${whitelistProgress}%` }"
                  ></div>
                </div>
                <div class="traffic-marks">
                  <span class="mark">0</span>
                  <span class="mark">{{ Math.floor(whiteList.total_purchased / 2) }}</span>
                  <span class="mark">{{ whiteList.total_purchased }}</span>
                </div>
              </div>
            </div>

            <div v-if="whiteList.first_bonus_given" class="bonus-badge">
              🎁 Бонус получен
            </div>

            <button
              v-if="whiteList.subscription_link"
              class="btn-small btn-primary"
              style="width: 100%; margin-top: 12px;"
              @click="handleWhitelistConnection"
            >
              🔗 Подключить
            </button>
            <button
              v-else
              class="btn-large"
              style="width: 100%; margin-top: 12px;"
              @click="handleBuyWhitelist"
            >
              💎 Купить трафик
            </button>
          </div>

          <!-- Empty state для белых списков -->
          <div v-else class="empty-state">
            <div class="empty-icon">🌐</div>
            <div class="empty-title">Нет активного белого списка</div>
            <div class="empty-text">Приобретите трафик для доступа к белому списку</div>
            <button class="btn-large" @click="handleBuyWhitelist" style="margin-top: 16px;">
              💎 Купить трафик
            </button>
          </div>
        </template>
      </template>
    </div>

    <!-- NEW: Welcome Popup -->
    <WelcomePopup
      :is-visible="showWelcomePopup"
      @close="closeWelcomePopup"
      @open-tasks="openTasksFromPopup"
    />

    <!-- NEW: Onboarding Tour Overlay -->
    <Teleport to="body">
      <div v-if="showOnboardingTour" class="onboarding-overlay" @click="skipTour">
        <div class="onboarding-spotlight" :style="spotlightStyle"></div>
        <div class="onboarding-tooltip" :style="tooltipStyle">
          <div class="tooltip-content">
            <div class="tooltip-icon">👆</div>
            <div class="tooltip-title">Нажмите "Подключить"</div>
            <div class="tooltip-text">Активируйте свой бесплатный пробный период прямо сейчас</div>
            <button class="tooltip-btn" @click.stop="skipTour">Понятно</button>
          </div>
        </div>
      </div>
    </Teleport>

    <div class="legal-links">
      <a href="https://tma.avevpn.com/privacy_policy_page.html" target="_blank" rel="noopener noreferrer">
        Политика конфиденциальности
      </a>
      <span class="separator">•</span>
      <a href="https://tma.avevpn.com/user_agreement_avevpn.html" target="_blank" rel="noopener noreferrer">
        Пользовательское соглашение
      </a>
    </div>

  </div>
</template>

<script setup>
import {ref, onMounted, onUnmounted, computed, watch, nextTick} from "vue";
import {useRouter, useRoute} from "vue-router";
import {haptic, disableSwipes} from "@/utils/telegram";
import WelcomePopup from "@/components/WelcomePopup.vue";
import { fetchAndCacheUserInfo } from '@/services/userService';

const router = useRouter();
const route = useRoute();

const balance = ref(null);
const freeTariff = ref(null);
const uniqueTariffs = ref([]);
const showWelcomePopup = ref(false);
const showOnboardingTour = ref(false);
const botName = ref("Prometei");

// ✅ Новые переменные для табов и белых списков
const activeTab = ref('subscriptions'); // 'subscriptions' или 'whitelist'
const whiteList = ref(null);
const shouldAnimateWhitelist = ref(false);
const whitelistProgress = ref(0);

const hasSessionData = ref(false);
const isInitialLoading = ref(true);
const isRefreshing = ref(false);

// Refs для элементов
const connectButtonRef = ref(null);
const freeTariffRef = ref(null);

// Notification swipe state
const isNotificationDismissed = ref(false);
const swipeOffset = ref(0);
const notificationOpacity = ref(1);
const touchStartX = ref(0);
const notificationRef = ref(null);

// Banner swipe state
const currentBannerIndex = ref(0);
const bannerOffset = ref(0);
const bannerTouchStartX = ref(0);
const bannerTouchCurrentX = ref(0);
const isBannerSwiping = ref(false);
const bannerAutoplayInterval = ref(null);
const bannersContainer = ref(null);

// Onboarding tour state
const spotlightStyle = ref({});
const tooltipStyle = ref({});

// Banners data
const banners = ref([
  {
    id: 1,
    image: '/200subsbanner.png',
    title: 'АКЦИЯ',
    route: 'buyVPN'
  },
  {
    id: 2,
    image: '/banner_ref.png',
    title: 'Реферальная программа',
    route: 'referal'
  },
  {
    id: 3,
    image: '/tasks_banner.png',
    title: 'Задания',
    route: 'tasks'
  }
]);

const isFreeTariffAvailable = computed(() => {
  return freeTariff.value && freeTariff.value.tariff_name;
});

const trafficPercentage = computed(() => {
  if (!freeTariff.value) return 0;

  const totalDays = 3;
  const totalHours = totalDays * 24;
  const tariffLeftString = freeTariff.value.tariff_left;

  const parts = tariffLeftString.split(' ');
  if (parts.length < 4) return 0;

  const daysLeft = parseInt(parts[0], 10) || 0;
  const hoursLeft = parseInt(parts[2], 10) || 0;

  const totalLeftHours = daysLeft * 24 + hoursLeft;
  let percentage = (totalLeftHours / totalHours) * 100;
  const buffer = Math.min(percentage * 0.1, 60);

  return Math.max(0, percentage - buffer);
});

const trafficBarClass = computed(() => {
  const daysLeft = parseInt(freeTariff.value?.tariff_left || 0);

  if (daysLeft <= 1) {
    return 'danger';
  } else if (daysLeft === 2) {
    return 'warning';
  } else {
    return 'success';
  }
});

const shouldShowTariffNotification = computed(() => {
  return uniqueTariffs.value.some(tariff => {
    const isExpired = tariff.daysRemaining <= 0;
    const isExpiringSoon = tariff.daysRemaining > 0 && tariff.daysRemaining <= 7;
    return isExpired || isExpiringSoon;
  });
});

const notificationTariff = computed(() => {
  const expired = uniqueTariffs.value.find(t => t.daysRemaining <= 0);
  if (expired) return expired;
  return uniqueTariffs.value.find(t => t.daysRemaining > 0 && t.daysRemaining <= 7);
});

const notificationTitle = computed(() => {
  if (!notificationTariff.value) return '';
  if (notificationTariff.value.daysRemaining <= 0) {
    return 'Тариф истёк';
  }
  return 'Тариф скоро истечёт';
});

const notificationText = computed(() => {
  if (!notificationTariff.value) return '';
  const tariffName = notificationTariff.value.name;
  if (notificationTariff.value.daysRemaining <= 0) {
    return `Тариф "${tariffName}" истёк. Продлите подписку`;
  }
  const days = notificationTariff.value.daysRemaining;
  const daysWord = getDaysWord(days);
  return `Тариф "${tariffName}" истекает через ${days} ${daysWord}`;
});

// ✅ NEW: Проверка нового пользователя с валидацией first_login
const isNewUser = computed(() => {
  const welcomeShown = localStorage.getItem("welcomeShown");

  if (welcomeShown === "true") {
    return false;
  }

  const firstLogin = sessionStorage.getItem("first_login");

  if (!firstLogin) {
    return false;
  }

  const firstLoginTime = parseInt(firstLogin) * 1000;
  const currentTime = Date.now();
  const threeHoursInMs = 3 * 60 * 60 * 1000;

  const isWithinThreeHours = (currentTime - firstLoginTime) <= threeHoursInMs;

  console.log("🔍 Проверка нового пользователя:", {
    welcomeShown,
    firstLogin,
    firstLoginTime: new Date(firstLoginTime),
    currentTime: new Date(currentTime),
    isWithinThreeHours
  });

  return isWithinThreeHours;
});

// ✅ Функция переключения табов с анимацией
const switchTab = (tab) => {
  haptic.impact();
  const previousTab = activeTab.value;
  activeTab.value = tab;
  sendMetric(`SwitchTab-${tab}`);
  console.log(`📑 Переключение на вкладку: ${tab}`);

  // Запуск анимации при переключении на белые списки
  if (tab === 'whitelist' && previousTab !== 'whitelist') {
    shouldAnimateWhitelist.value = false;
    whitelistProgress.value = 0;

    nextTick(() => {
      setTimeout(() => {
        shouldAnimateWhitelist.value = true;
        whitelistProgress.value = calculateWhitelistProgress();
      }, 100);
    });
  }
};

// ✅ Расчет прогресса для белых списков
const calculateWhitelistProgress = () => {
  if (!whiteList.value || whiteList.value.total_purchased === 0) return 0;
  return Math.min(100, (whiteList.value.gb_left / whiteList.value.total_purchased) * 100);
};

// ✅ Обработчики для белых списков
const handleWhitelistConnection = () => {
  console.log("🔗 Клик по Подключить белый список");
  sendMetric('Connect-Whitelist');
  goTo('connection');
};

const handleBuyWhitelist = () => {
  console.log("💎 Клик по Купить трафик");
  sendMetric('BuyWhitelist');
  goTo('buyVPN'); // или другой роут для покупки белых списков
};

watch(() => route.path, (newPath, oldPath) => {
  console.log(`📍 Смена маршрута: ${oldPath} → ${newPath}`);

  if (oldPath === '/' && newPath !== '/') {
    if (showWelcomePopup.value) {
      console.log("👋 Закрываем попап при смене вкладки");
      showWelcomePopup.value = false;
      localStorage.setItem("welcomeShown", "true");
    }
    if (showOnboardingTour.value) {
      console.log("🎯 Закрываем тур при смене вкладки");
      showOnboardingTour.value = false;
    }
  }
});

const getCountriesText = (tariffName) => {
  if (tariffName.includes('Basic')) return '4 страны';
  if (tariffName.includes('Medium')) return '8 стран';
  if (tariffName.includes('Premium')) return '12 стран';
  return 'Несколько стран';
};

const getDevicesText = (tariffName) => {
  if (tariffName.includes('Basic')) return '1 устройство';
  if (tariffName.includes('Medium')) return '2 устройства';
  if (tariffName.includes('Premium')) return '10 устройств';
  return '1 устройство';
};

const calculateProgress = (daysRemaining) => {
  const maxDays = 30;
  return Math.min(100, (daysRemaining / maxDays) * 100);
};

const addEmojiToTariff = (tariffName) => {
  if (tariffName.includes('Free')) return 'Free 🌱';
  if (tariffName.includes('Basic')) return 'Basic 🧵';
  if (tariffName.includes('Medium')) return 'Medium 🚀';
  if (tariffName.includes('Premium')) return 'Premium 💎';
  return tariffName;
};

// Banner functions
const getBannerWidth = () => {
  if (!bannersContainer.value) return 0;
  return bannersContainer.value.offsetWidth;
};

const updateBannerOffset = (animated = true) => {
  const width = getBannerWidth();
  const targetOffset = -currentBannerIndex.value * width;

  if (animated) {
    bannerOffset.value = targetOffset;
  } else {
    bannerOffset.value = targetOffset;
  }
};

const handleBannerTouchStart = (e) => {
  stopBannerAutoplay();
  isBannerSwiping.value = true;
  bannerTouchStartX.value = e.touches[0].clientX;
  bannerTouchCurrentX.value = e.touches[0].clientX;
};

const handleBannerTouchMove = (e) => {
  if (!isBannerSwiping.value) return;

  bannerTouchCurrentX.value = e.touches[0].clientX;
  const diff = bannerTouchCurrentX.value - bannerTouchStartX.value;
  const width = getBannerWidth();
  const baseOffset = -currentBannerIndex.value * width;

  bannerOffset.value = baseOffset + diff;
};

const handleBannerTouchEnd = () => {
  if (!isBannerSwiping.value) return;

  const diff = bannerTouchCurrentX.value - bannerTouchStartX.value;
  const width = getBannerWidth();
  const threshold = width * 0.3;

  if (Math.abs(diff) > threshold) {
    if (diff > 0 && currentBannerIndex.value > 0) {
      currentBannerIndex.value--;
    } else if (diff < 0 && currentBannerIndex.value < banners.value.length - 1) {
      currentBannerIndex.value++;
    }
  }

  updateBannerOffset(true);
  isBannerSwiping.value = false;
  startBannerAutoplay();
};

const goToBanner = (index) => {
  stopBannerAutoplay();
  currentBannerIndex.value = index;
  updateBannerOffset(true);
  startBannerAutoplay();
};

const nextBanner = () => {
  currentBannerIndex.value = (currentBannerIndex.value + 1) % banners.value.length;
  updateBannerOffset(true);
};

const startBannerAutoplay = () => {
  stopBannerAutoplay();
  bannerAutoplayInterval.value = setInterval(() => {
    nextBanner();
  }, 5000);
};

const stopBannerAutoplay = () => {
  if (bannerAutoplayInterval.value) {
    clearInterval(bannerAutoplayInterval.value);
    bannerAutoplayInterval.value = null;
  }
};

const handleBannerClick = (banner) => {
  haptic.impact();
  sendMetric(`Banner-${banner.title}`);
  router.push(`/${banner.route}`);
};

const loadDataFromSession = () => {
  const sessionBalance = sessionStorage.getItem("balance");
  const sessionAllTariffs = sessionStorage.getItem("all_tariffs");
  const sessionWhiteList = sessionStorage.getItem("white_list");

  if (sessionBalance !== null) {
    balance.value = parseFloat(sessionBalance);
    hasSessionData.value = true;
  }

  // ✅ Загружаем white_list из sessionStorage
  if (sessionWhiteList) {
    try {
      whiteList.value = JSON.parse(sessionWhiteList);
      console.log("✅ White list загружен из sessionStorage:", whiteList.value);
    } catch (error) {
      console.error("❌ Ошибка при парсинге white_list:", error);
    }
  }

  if (sessionAllTariffs) {
    try {
      const tariffsData = JSON.parse(sessionAllTariffs);
      const tempTariffs = [];

      ['basic', 'medium', 'premium'].forEach(type => {
        if (tariffsData[type]?.expiry) {
          const expiry = parseInt(tariffsData[type].expiry);
          const endDate = new Date(expiry * 1000);
          const timeDifference = endDate - new Date();
          const daysRemaining = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

          const tariffName = type.charAt(0).toUpperCase() + type.slice(1);

          tempTariffs.push({
            name: addEmojiToTariff(tariffName),
            expiry: expiry,
            subscription_link: tariffsData[type].link || '',
            daysRemaining: daysRemaining,
            subscriptionEnd: daysRemaining > 0
                ? `Осталось ${daysRemaining} ${getDaysWord(daysRemaining)}`
                : "Подписка истекла"
          });
        }
      });

      uniqueTariffs.value = tempTariffs;

      if (tariffsData.free?.expiry) {
        const freeExpiry = parseInt(tariffsData.free.expiry);
        const currentTime = Math.floor(Date.now() / 1000);
        const timeLeft = freeExpiry - currentTime;

        if (timeLeft > 0) {
          const daysLeft = Math.floor(timeLeft / 86400);
          const hoursLeft = Math.floor((timeLeft % 86400) / 3600);

          freeTariff.value = {
            tariff_name: "Free 🌱",
            tariff_left: `${daysLeft} дней ${hoursLeft} часов`,
            subscription_link: tariffsData.free.link || ''
          };
        }
      }

      hasSessionData.value = true;
    } catch (error) {
      console.error("Ошибка при загрузке тарифов из sessionStorage:", error);
    }
  }

  isInitialLoading.value = false;
};

const refreshDataInBackground = () => {
  console.log("🔄 Запускаем фоновое обновление данных...");
  isRefreshing.value = true;

  fetchAndCacheUserInfo(true)
      .then(() => {
        console.log("✅ Данные обновлены, перезагружаем из sessionStorage");
        loadDataFromSession();
      })
      .catch((error) => {
        console.error("❌ Ошибка при обновлении данных в фоне:", error);
      })
      .finally(() => {
        isRefreshing.value = false;
      });
};

const sendMetric = (buttonName) => {
  try {
    const token = sessionStorage.getItem("jwtToken");
    if (!token) {
      console.error("JWT токен не найден для отправки метрики");
      return;
    }

    const eventname = `Home-${buttonName}-${botName.value}`;

    fetch('https://baaack.zabanishumresh.com/addevent', {
      method: 'POST',
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({eventname}),
      keepalive: true
    })
        .then(response => {
          if (response.ok) {
            console.log(`📊 Метрика отправлена: ${eventname}`);
          } else {
            console.error(`❌ Ошибка отправки метрики: ${response.status}`);
          }
        })
        .catch(error => {
          console.error("Ошибка при отправке метрики:", error);
        });
  } catch (error) {
    console.error("Ошибка при отправке метрики:", error);
  }
};

const goTo = (route) => {
  console.log(`🔗 Переход на маршрут: ${route}`);
  haptic.impact();
  router.push(`/${route}`);
};

const handleDepositClick = () => {
  console.log("💳 Клик по кнопке Пополнить");
  sendMetric('Deposit-btn');
  goTo('deposit');
};

const handlePaymentHistoryClick = () => {
  console.log("📋 Клик по истории платежей");
  sendMetric('PaymentHistory-btn');
  goTo('payment-history');
};

const handleConnectionClick = (tariffName) => {
  console.log(`🔗 Клик по Подключить: ${tariffName}`);
  sendMetric(`Connect-${tariffName}`);

  if (showOnboardingTour.value) {
    skipTour();
  }

  goTo('connection');
};

const handleBuySubscriptionClick = () => {
  console.log("🛒 Клик по Купить подписку");
  sendMetric('BuySubscription');
  goTo('buyVPN');
};

const handleReferralClick = () => {
  console.log("🎁 Клик по Реферальной программе");
  sendMetric('Referral');
  goTo('referal');
};

const handleTasksClick = () => {
  console.log("✨ Клик по Заданиям");
  sendMetric('Tasks');
  goTo('tasks');
};

const handleTouchStart = (e) => {
  touchStartX.value = e.touches[0].clientX;
};

const handleTouchMove = (e) => {
  const currentX = e.touches[0].clientX;
  const diff = currentX - touchStartX.value;

  if (diff < 0) {
    swipeOffset.value = diff;
    notificationOpacity.value = 1 + (diff / 300);
  }
};

const handleTouchEnd = () => {
  if (swipeOffset.value < -100) {
    dismissNotification();
  } else {
    swipeOffset.value = 0;
    notificationOpacity.value = 1;
  }
};

const dismissNotification = () => {
  sendMetric('DismissNotification');
  swipeOffset.value = -500;
  notificationOpacity.value = 0;
  setTimeout(() => {
    isNotificationDismissed.value = true;
  }, 300);
};

const closeWelcomePopup = () => {
  console.log("👋 Закрываем Welcome Popup");
  showWelcomePopup.value = false;
  localStorage.setItem("welcomeShown", "true");
  sendMetric('WelcomePopup-Closed');

  nextTick(() => {
    if (route.path === '/' && isNewUser.value && isFreeTariffAvailable.value) {
      console.log("🎯 Условия для тура выполнены, запускаем через 500ms");
      setTimeout(() => {
        if (route.path === '/') {
          startOnboardingTour();
        }
      }, 500);
    }
  });
};

const openTasksFromPopup = () => {
  showWelcomePopup.value = false;
  localStorage.setItem("welcomeShown", "true");
  sendMetric('WelcomePopup-OpenTasks');
  goTo('tasks');
};

const startOnboardingTour = () => {
  console.log("🎯 Попытка запустить Onboarding Tour...");

  if (!connectButtonRef.value) {
    console.error("❌ connectButtonRef не найден");
    return;
  }

  if (!freeTariffRef.value) {
    console.error("❌ freeTariffRef не найден");
    return;
  }

  console.log("✅ Refs найдены, получаем координаты");

  try {
    const buttonRect = connectButtonRef.value.getBoundingClientRect();
    const cardRect = freeTariffRef.value.getBoundingClientRect();

    console.log("📐 Button rect:", buttonRect);
    console.log("📐 Card rect:", cardRect);

    spotlightStyle.value = {
      top: `${cardRect.top - 8}px`,
      left: `${cardRect.left - 8}px`,
      width: `${cardRect.width + 16}px`,
      height: `${cardRect.height + 16}px`,
    };

    tooltipStyle.value = {
      top: `${buttonRect.bottom + 20}px`,
      left: '50%',
      transform: 'translateX(-50%)',
    };

    console.log("🎨 Spotlight style:", spotlightStyle.value);
    console.log("🎨 Tooltip style:", tooltipStyle.value);

    showOnboardingTour.value = true;
    sendMetric('OnboardingTour-Started');
    console.log("✅ Onboarding Tour запущен!");
  } catch (error) {
    console.error("❌ Ошибка при запуске тура:", error);
  }
};

const skipTour = () => {
  console.log("⏭ Пропускаем тур");
  showOnboardingTour.value = false;
  localStorage.setItem("welcomeShown", "true");
  sendMetric('OnboardingTour-Skipped');
};

watch(isFreeTariffAvailable, (newVal) => {
  console.log("👀 isFreeTariffAvailable изменился:", newVal);

  if (newVal && isNewUser.value && !showWelcomePopup.value && !showOnboardingTour.value && route.path === '/') {
    console.log("🚀 Автозапуск тура через watch");
    nextTick(() => {
      setTimeout(() => {
        if (route.path === '/') {
          startOnboardingTour();
        }
      }, 800);
    });
  }
});

function getDaysWord(days) {
  if (days % 10 === 1 && days % 100 !== 11) {
    return "День";
  } else if (
      [2, 3, 4].includes(days % 10) &&
      ![12, 13, 14].includes(days % 100)
  ) {
    return "Дня";
  } else {
    return "Дней";
  }
}

onMounted(() => {
  console.log("🚀 Home.vue mounted");

  disableSwipes();

  isNotificationDismissed.value = false;
  swipeOffset.value = 0;
  notificationOpacity.value = 1;

  console.log("📦 Загружаем данные из sessionStorage...");
  loadDataFromSession();

  startBannerAutoplay();

  window.addEventListener('resize', () => updateBannerOffset(false));

  const isTelegramWebApp = window.Telegram && window.Telegram.WebApp;

  if (isTelegramWebApp) {
    const webApp = window.Telegram.WebApp;
    const initData = webApp.initData || "";

    if (initData === "") {
      router.push('/welcome');
      return;
    }

    const telegramId = webApp.initDataUnsafe.user?.id;
    webApp.setHeaderColor("#1a1a2e");
    sessionStorage.setItem("tg_id", telegramId);

    console.log("⏳ Запускаем фоновое обновление после рендера...");

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        console.log("🔄 Фоновое обновление (requestIdleCallback)");
        refreshDataInBackground();
      }, {timeout: 500});
    } else {
      setTimeout(() => {
        console.log("🔄 Фоновое обновление (setTimeout)");
        refreshDataInBackground();
      }, 100);
    }

    if (isNewUser.value) {
      console.log("👋 Новый пользователь, показываем Welcome Popup");
      showWelcomePopup.value = true;
      sendMetric('WelcomePopup-Shown');
    } else {
      console.log("👤 Существующий пользователь, пропускаем Welcome Popup");
    }
  } else {
    router.push('/welcome');
  }
});

onUnmounted(() => {
  stopBannerAutoplay();
  window.removeEventListener('resize', () => updateBannerOffset(false));
});
</script>

<style scoped>
.container {
  width: 95%;
  height: 85%;
  padding: 20px 10px 100px 10px;
  border-radius: 20px;
  overflow-y: auto;
}

/* Balance Card */
.balance-card {
  background: rgba(26, 31, 41, 0.6);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3),
  0 0 0 0.5px rgba(255, 255, 255, 0.05) inset;
}

.balance-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.history-btn {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.history-btn:active {
  transform: scale(0.9);
  background: rgba(255, 255, 255, 0.2);
}

.balance-label {
  font-size: 13px;
  opacity: 0.9;
  font-weight: 500;
}

.balance-amount {
  font-size: 42px;
  font-weight: 800;
  letter-spacing: -1px;
  margin-bottom: 4px;
}

.balance-btn {
  background: rgba(255, 107, 53, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 107, 53, 0.3);
  color: #FF8555;
  padding: 14px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.balance-btn:active {
  transform: scale(0.97);
  background: rgba(255, 107, 53, 0.25);
}

/* Banners Section */
.banners-section {
  margin-bottom: 16px;
}

.banners-container {
  width: 100%;
  overflow: hidden;
  border-radius: 16px;
  position: relative;
}

.banners-track {
  display: flex;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

.banner-item {
  min-width: 100%;
  flex-shrink: 1;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  position: relative;
}

.banner-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 16px;
  object-fit: cover;
  aspect-ratio: 1280 / 442;
  background: rgba(26, 31, 41, 0.6);
  backdrop-filter: blur(20px);
}

.banner-dots {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  gap: 8px;
  z-index: 10;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 20px;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot.active {
  background: #FF8555;
  width: 20px;
  border-radius: 3px;
}

.dot:active {
  transform: scale(0.9);
}

/* Tariff Notification (Swipeable) */
.tariff-notification {
  background: rgba(255, 152, 0, 0.15);
  backdrop-filter: blur(15px) saturate(180%);
  -webkit-backdrop-filter: blur(15px) saturate(180%);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  box-shadow: 0 4px 20px rgba(255, 152, 0, 0.15);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
  opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  touch-action: pan-y;
  position: relative;
  overflow: hidden;
}

.tariff-notification::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #fbbf24, #f59e0b);
}

.notification-icon {
  font-size: 24px;
  flex-shrink: 0;
  line-height: 1;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 14px;
  font-weight: 700;
  color: #fbbf24;
  margin-bottom: 4px;
  line-height: 1.2;
}

.notification-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.4;
}

.notification-close {
  width: 24px;
  height: 24px;
  background: rgba(255, 152, 0, 0.2);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 50%;
  font-size: 14px;
  color: #fbbf24;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  line-height: 1;
}

.notification-close:active {
  transform: scale(0.9);
  background: rgba(255, 152, 0, 0.3);
}

/* Card */
.card {
  background: rgba(26, 31, 41, 0.6);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3),
  0 0 0 0.5px rgba(255, 255, 255, 0.05) inset;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.card-title-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
}

/* ========== ТАБЫ ПЕРЕКЛЮЧЕНИЯ (Apple Liquid Glass) ========== */
.tabs-switcher {
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-radius: 14px;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  position: relative;
}

.tab-btn {
  flex: 1;
  padding: 12px 16px;
  background: transparent;
  border: none;
  border-radius: 11px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
}

.tab-btn.active {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.25) 0%,
    rgba(255, 255, 255, 0.15) 100%
  );
  backdrop-filter: blur(30px) saturate(200%);
  -webkit-backdrop-filter: blur(30px) saturate(200%);
  color: white;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transform: scale(1.02);
}

.tab-btn.active::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 11px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.2) 0%,
    transparent 50%,
    rgba(0, 0, 0, 0.1) 100%
  );
  pointer-events: none;
}

.tab-btn:active:not(.active) {
  transform: scale(0.98);
  background: rgba(255, 255, 255, 0.05);
}

/* Анимация переключения */
@keyframes liquidShift {
  0% {
    transform: scale(1);
    filter: brightness(1);
  }
  50% {
    transform: scale(1.05);
    filter: brightness(1.1);
  }
  100% {
    transform: scale(1.02);
    filter: brightness(1);
  }
}

.tab-btn.active {
  animation: liquidShift 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-title {
  font-size: 16px;
  font-weight: 700;
}

.refresh-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #FF8555;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Subscription Item */
.sub-item {
  background: rgba(36, 43, 56, 0.6);
  backdrop-filter: blur(15px) saturate(180%);
  -webkit-backdrop-filter: blur(15px) saturate(180%);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.sub-item:last-child {
  margin-bottom: 0;
}

.sub-item:active {
  transform: scale(0.98);
}

/* ========== БЕЛЫЕ СПИСКИ ========== */
.whitelist-item {
  border-left: 3px solid #4ade80;
}

.sub-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.sub-name {
  font-size: 16px;
  font-weight: 700;
}

.sub-badge {
  background: rgba(74, 222, 128, 0.15);
  color: #4ade80;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.sub-badge.inactive {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
}

.sub-info {
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.sub-info-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.sub-progress {
  margin-top: 12px;
  margin-bottom: 12px;
}

.progress-bar-bg {
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 6px;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #FF6B35, #F7931E);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-bar-fill.danger {
  background: linear-gradient(90deg, #ff0000, #ff4d4d);
}

.progress-bar-fill.warning {
  background: linear-gradient(90deg, #ff7f00, #f6a152);
}

.progress-bar-fill.success {
  background: linear-gradient(90deg, #67c289, #3d8a57);
}

.progress-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  text-align: right;
}

/* ========== ИНДИКАТОР ТРАФИКА ========== */
.traffic-indicator {
  margin: 16px 0;
}

.traffic-label {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 12px;
}

.traffic-amount {
  font-size: 32px;
  font-weight: 800;
  background: linear-gradient(135deg, #4ade80, #22c55e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
}

.traffic-total {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 600;
}

.traffic-bar-wrapper {
  position: relative;
}

.traffic-bar-bg {
  height: 12px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
}

.traffic-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #22c55e, #4ade80, #86efac);
  border-radius: 20px;
  position: relative;
  transition: width 0.3s ease;
  box-shadow:
    0 0 20px rgba(74, 222, 128, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.traffic-bar-fill::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.3) 0%,
    transparent 50%,
    rgba(0, 0, 0, 0.1) 100%
  );
  border-radius: 20px;
}

.traffic-bar-fill::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 100px;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* ✅ Анимация заполнения при открытии вкладки */
.traffic-bar-fill.animate {
  animation: fillBar 1.2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes fillBar {
  0% {
    width: 0 !important;
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 1;
  }
}

.traffic-marks {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  padding: 0 2px;
}

.mark {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
  font-weight: 600;
}

/* ========== БОНУС БЕЙДЖ ========== */
.bonus-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(251, 191, 36, 0.15);
  border: 1px solid rgba(251, 191, 36, 0.3);
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  color: #fbbf24;
  font-weight: 600;
  margin-top: 12px;
}

/* ========== КНОПКА "ПОДКЛЮЧИТЬ" ========== */
.btn-small {
  padding: 2px;
  background: linear-gradient(135deg, #FF66DD, #FF3399, #DD0077);
  border-radius: 14px;
  position: relative;
  border: none;
  cursor: pointer;
}

.btn-small::before {
  content: '';
  position: absolute;
  inset: -2px;
  background: linear-gradient(135deg, #FF66DD, #FF3399, #DD0077);
  border-radius: 14px;
  opacity: 0.6;
  filter: blur(10px);
  z-index: -1;
}

.btn-primary {
  width: 100%;
  background: linear-gradient(135deg, #FF66DD 0%, #FF3399 50%, #DD0077 100%);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 20px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow:
    0 8px 24px rgba(255, 102, 221, 0.4),
    inset 0 1px 1px rgba(255, 255, 255, 0.3),
    inset 0 -1px 1px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  letter-spacing: 0.5px;
}

.btn-primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.25) 0%, transparent 100%);
  border-radius: 12px 12px 0 0;
  z-index: 0;
}

.btn-primary:active {
  transform: scale(0.97);
  box-shadow:
    0 4px 16px rgba(255, 102, 221, 0.5),
    inset 0 1px 1px rgba(255, 255, 255, 0.2);
}

.btn-large {
  background: linear-gradient(135deg, #FF6B35, #F7931E);
  color: white;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(255, 107, 53, 0.3);
}

.btn-large:active {
  transform: scale(0.98);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 40px 20px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
}

.empty-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 20px;
}

/* ========== ONBOARDING TOUR ========== */
.onboarding-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9998;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.onboarding-spotlight {
  position: absolute;
  background: transparent;
  border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.85);
  z-index: 9999;
  pointer-events: none;
  transition: all 0.3s ease;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.85),
                0 0 20px 4px rgba(255, 102, 221, 0.6);
  }
  50% {
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.85),
                0 0 30px 8px rgba(255, 102, 221, 0.8);
  }
}

.onboarding-tooltip {
  position: fixed;
  background: linear-gradient(135deg, rgba(255, 102, 221, 0.95), rgba(255, 51, 153, 0.95));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 24px;
  z-index: 10000;
  max-width: 320px;
  box-shadow: 0 20px 60px rgba(255, 102, 221, 0.4),
              inset 0 1px 1px rgba(255, 255, 255, 0.3);
  animation: slideUp 0.4s ease;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.tooltip-content {
  text-align: center;
}

.tooltip-icon {
  font-size: 48px;
  margin-bottom: 12px;
  animation: bounce 1s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.tooltip-title {
  font-size: 20px;
  font-weight: 800;
  color: white;
  margin-bottom: 8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.tooltip-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.5;
  margin-bottom: 16px;
}

.tooltip-btn {
  background: white;
  color: #FF3399;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.tooltip-btn:active {
  transform: scale(0.96);
}

/* Legal Links */
.legal-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  margin-top: 20px;
  font-size: 11px;
  color: #888;
  flex-wrap: wrap;
}

.legal-links a {
  color: #888;
  text-decoration: none;
  transition: color 0.2s ease;
}

.legal-links a:hover {
  color: #75d8b9;
}

.legal-links .separator {
  color: #555;
}

/* Skeleton */
.skeleton {
  position: relative;
  overflow: hidden;
  background: #1f1f1f;
  border-radius: 15px;
  -webkit-mask-image: -webkit-radial-gradient(white, black);
}

.skeleton::after {
  content: "";
  border-radius: inherit;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateX(-100%);
  background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0,
      rgba(255, 255, 255, 0.05) 20%,
      rgba(255, 255, 255, 0.1) 60%,
      rgba(255, 255, 255, 0)
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
</style>